<template>
  <v-container
    class="d-flex"
    fluid
    style="min-height: 100%;"
  >
    <v-row>
      <v-col>
        <v-container fluid>
          <v-row>
            <v-col
              cols="12"
            >
              <v-select
                v-model="docketEntry.docket_entry_recipient.party_id"
                :disabled="!!docketEntryEditorDocketEntryId"
                :items="partyOptions"
                label="Recipient *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.docket_entry_recipient.party_id)"
                @input="_ => v$.docketEntry.docket_entry_recipient.party_id.$touch()"
                @blur="_ => v$.docketEntry.docket_entry_recipient.party_id.$touch()"
              />
            </v-col>
            <v-col
              v-if="!docketEntry.id"
              cols="12"
            >
              <v-checkbox
                v-model="sendNow"
                dense
                hide-details
                label="Send this text message now"
              />
            </v-col>
            <v-col
              v-if="!sendNow"
              cols="12"
              sm="6"
            >
              <DateField
                v-model="docketEntry.action_date"
                label="Scheduled Date *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.action_date)"
                @input="_ => v$.docketEntry.action_date.$touch()"
                @blur="_ => v$.docketEntry.action_date.$touch()"
              />
            </v-col>
            <v-col
              v-if="!sendNow"
              class="dateField xs"
              cols="6"
            >
              <TimeField
                v-model="docketEntry.event_time_start"
                label="Scheduled Time *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.event_time_start)"
                @input="_ => v$.docketEntry.event_time_start.$touch()"
                @blur="_ => v$.docketEntry.event_time_start.$touch()"
              />
            </v-col>
            <v-col
              v-if="!sendNow"
              cols="12"
              class="dateField xs"
            >
              <v-autocomplete
                v-model="docketEntry.timezone"
                :items="timezoneOptions"
                label="Timezone *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.timezone)"
                @input="_ => v$.docketEntry.timezone.$touch()"
                @blur="_ => v$.docketEntry.timezone.$touch()"
              />
            </v-col>
            <v-col cols="12">
              <v-btn
                depressed
                @click="showTemplateSelector = true"
              >
                Load message from Template
              </v-btn>
              <TextMessageEditorTemplateSelector
                v-if="showTemplateSelector"
                v-model="showTemplateSelector"
                @set-message-text="text => setMessageText(text)"
              />
            </v-col>
            <v-col
              v-if="commentHasPlaceholders"
              cols="12"
            >
              <v-alert
                dense
                text
                type="warning"
              >
                No variables apply to this text messages. Make sure to remove them.
              </v-alert>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="docketEntry.comment"
                counter
                :counter-value="value => commentCharCounterText(value)"
                label="Text to be sent *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.comment)"
                @input="_ => v$.docketEntry.comment.$touch()"
                @blur="_ => v$.docketEntry.comment.$touch()"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { useVuelidate } from '@vuelidate/core'

import { formattedPhoneNumber, partyName } from '@/utilities'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'

import DateField from '@/components/Fields/DateField.vue'
import TimeField from '@/components/Fields/TimeField.vue'

import TextMessageEditorTemplateSelector from './TextMessageEditorTemplateSelector.vue'

const COMMENT_MAX_SIZE = 512

export default {
  name: 'TextMessageEditorOverview',

  components: {
    DateField,
    TextMessageEditorTemplateSelector,
    TimeField,
  },

  props: {
    // eslint-disable-next-line vue/no-unused-properties
    sendImmediately: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      replaceableValues: [],
      selectedAvailabilitySlotId: null,
      selectedDefaultValues: [],
      selectedParties: [],
      selectedUsers: [],
      sendNow: false,
      showTemplateSelector: false,
      timezoneOptions: [],
      usersThatNeedSlots: [],
    }
  },

  validations () {
    return {
      docketEntry: {
        action_date: {
          required: requiredIf(!this.sendNow),

          timezoneSelected: helpers.withMessage(
            'Please select a timezone',
            (value, _, vm) => vm.isTimezoneValid
          ),

          validDate: helpers.withMessage(
            'Delivery date cannot be in the past',
            (value, _, vm) => !vm.isDeliveryDateInPast
          ),
        },

        comment: {
          maxLength: maxLength(COMMENT_MAX_SIZE),
          required,
        },

        docket_entry_recipient: {
          party_id: {
            required,
          },
        },

        event_time_start: {
          required: requiredIf(!this.sendNow),

          timezoneSelected: helpers.withMessage(
            'Please select a timezone',
            (value, _, vm) => vm.isTimezoneValid
          ),

          validDate: helpers.withMessage(
            'Delivery date/time cannot be in the past',
            (value, _, vm) => !vm.isDeliveryDateTimeInPast
          ),
        },

        timezone: {
          required: requiredIf(!this.sendNow),
        },
      },
    }
  },

  computed: {
    commentHasPlaceholders () {
      return !!this.docketEntry.comment.match(/\[[^\]]+\]/)?.length
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    isDeliveryDateInPast () {
      if (this.sendNow) return false
      if (!this.docketEntry.timezone) return false

      const startDate = moment.tz(`${this.docketEntry.action_date}`, 'YYYY-MM-DD', this.docketEntry.timezone)
      const currentDate = moment.tz(moment(), this.docketEntry.timezone).startOf('day')

      return startDate.isBefore(currentDate)
    },

    isDeliveryDateTimeInPast () {
      if (this.sendNow) return false
      if (!this.docketEntry.timezone) return false

      const startDate = moment.tz(
        `${this.docketEntry.action_date} ${this.docketEntry.event_time_start}`,
        'YYYY-MM-DD hh:mm a',
        this.docketEntry.timezone
      )

      const minDate = moment.tz(moment(), this.docketEntry.timezone).subtract(5, 'minutes')

      return startDate.isBefore(minDate)
    },

    isFormValid () {
      return !this.v$.$invalid
    },

    isTimezoneValid () {
      return !!this.docketEntry.timezone
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties || []
    },

    partyOptions () {
      return (this.legalCaseParties || []).reduce((res, party) => {
        const preferredText = party.phones.find(p => p.preferred_text)
        if (!preferredText) return res

        res.push({
          text: `${partyName(party)} (${formattedPhoneNumber(preferredText)})`,
          value: party.id,
        })

        return res
      }, [])
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'docketEntryEditorDocketEntryId',
      'editedDocketEntry',
      'originalDocketEntry',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),
  },

  watch: {
    'docketEntry.timezone' (val) {
      if (!this.timezoneOptions.find(tzo => tzo.value === val)) {
        this.docketEntry.timezone = this.originalDocketEntry.timezone
      }
    },

    isFormValid: {
      handler (value) {
        this.$emit('update:valid', value)
      },

      immediate: true,
    },

    sendNow: {
      handler (value) {
        this.$emit('update:send-immediately', value)
      },

      immediate: true,
    },
  },

  async created () {
    await this.loadTimezoneOptions()
  },

  methods: {
    commentCharCounterText (value) {
      return `${value.length} / ${COMMENT_MAX_SIZE}`
    },

    getErrorMessages (validator) {
      return validator?.$errors.map(e => e.$message)
    },

    async loadTimezoneOptions () {
      this.timezoneOptions = await this.getOptions({ field: 'timezone', model: 'SiteSetting::Timezone' })
    },

    setMessageText (text) {
      this.docketEntry.comment = text
    },

    async validate () {
      await this.v$.$validate()
    },

    ...mapActions(SHARED_NAMESPACE, [
      'getOptions',
    ]),
  },
}
</script>
